import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';
import { COLORS, FONT_WEIGHTS, TEXT_SIZES } from '../../../utils/constants';
import CustomButton from '../../common/Button';
import { TournamentFormatEnum, TournamentType } from '../../../enums/enums';
import { useSnackbar } from '../../../contexts/SnackbarContext/SnackbarProvider';
import {
  generateBracketRequest,
  getPairedUnpairedParticipantsRequest,
} from '../../../api/requests/tournaments';
import { TournamentDetailsData } from '../../../types/types';
import { useEventContext } from '../../../contexts/EventContext';
import SportTypeModal from './SportTypeModal';

const ToggleSections: React.FC<{
  activeSection: 'paired' | 'unpaired';
  setActiveSection: (section: 'paired' | 'unpaired') => void;
  pairedCount: number;
  unpairedCount: number;
}> = ({ activeSection, setActiveSection, pairedCount, unpairedCount }) => {
  return (
    <ToggleContainer>
      <Section
        active={activeSection === 'unpaired'}
        onClick={() => setActiveSection('unpaired')}
      >
        <SectionText>Unpaired Players</SectionText>
        <Badge>
          <BadgeText>{unpairedCount ?? 0}</BadgeText>
        </Badge>
      </Section>
      <Section
        active={activeSection === 'paired'}
        onClick={() => setActiveSection('paired')}
      >
        <SectionText>Paired Players</SectionText>
        <Badge>
          <BadgeText>{pairedCount ?? 0}</BadgeText>
        </Badge>
      </Section>
    </ToggleContainer>
  );
};

const PlayerCard: React.FC<{
  name: string;
  email: string;
  phone: string;
}> = ({ name, email, phone }) => {
  const initials = name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .toUpperCase();
  return (
    <PlayerCardContainer>
      <AvatarContainer>
        <InitialsText>{initials}</InitialsText>
      </AvatarContainer>
      <PlayerInfoContainer>
        <PlayerNameText>{name}</PlayerNameText>
        <PlayerEmailText>{email}</PlayerEmailText>
        <PlayerPhoneText>{phone}</PlayerPhoneText>
      </PlayerInfoContainer>
    </PlayerCardContainer>
  );
};

const PairedPlayerCard: React.FC<{
  pairNumber: number;
  player1: string;
  player2: string;
}> = ({ pairNumber, player1, player2 }) => {
  return (
    <PairedCard>
      <NumberCircle>
        <NumberText>{pairNumber}</NumberText>
      </NumberCircle>
      <NameText>
        {player1} & {player2}
      </NameText>
    </PairedCard>
  );
};

const defaultData: TournamentDetailsData = {
  totalRegisteredCount: 0,
  totalPairedCount: 0,
  totalUnpairedCount: 0,
  unpairedParticipants: [],
  pairedParticipants: [],
};

// Main Component
const DoublesPlayersDetails: React.FC = () => {
  const navigate = useNavigate();
  const { tournamentId } = useParams();
  const { tournamentType, refreshEventData } = useEventContext();
  const snackbar = useSnackbar();
  const [tournamentDetails, setTournamentDetails] =
    useState<TournamentDetailsData | null>(defaultData);
  const [activeSection, setActiveSection] = useState<'paired' | 'unpaired'>(
    'unpaired',
  );
  const [isBracketModalVisible, setIsBracketModalVisible] = useState(false);

  const fetchTournamentDetails = async () => {
    const response = await getPairedUnpairedParticipantsRequest(
      Number(tournamentId),
    );
    if (response && response.isSuccess) {
      setTournamentDetails(response.value || defaultData);
    }
  };

  useEffect(() => {
    fetchTournamentDetails();
  }, []);

  const handleGenerateBracket = async (
    selectedFormat: TournamentFormatEnum,
  ) => {
    try {
      const response = await generateBracketRequest(
        Number(tournamentId),
        selectedFormat,
      );
      if (response && response.isSuccess) {
        snackbar.success('Bracket generated successfully!');
        setIsBracketModalVisible(false);
        // Navigate to brackets view after successful generation
        navigate('../doubles-brackets', { relative: 'path' });
        refreshEventData();
      } else {
        snackbar.error(response?.error);
      }
    } catch (error) {
      console.error('Error generating bracket:', error);
      snackbar.error('Failed to generate bracket. Please try again.');
    }
  };

  const parsedTournamentType = tournamentType
    ? parseInt(tournamentType)
    : TournamentType.Singles;

  return (
    <Container>
      <BodyContainer>
        <ToggleSections
          activeSection={activeSection}
          setActiveSection={setActiveSection}
          pairedCount={tournamentDetails?.totalPairedCount ?? 0}
          unpairedCount={tournamentDetails?.totalUnpairedCount ?? 0}
        />

        <ScrollContainer>
          {activeSection === 'unpaired' ? (
            tournamentDetails?.unpairedParticipants &&
            tournamentDetails.unpairedParticipants.length > 0 ? (
              tournamentDetails.unpairedParticipants.map((player) => (
                <PlayerCard
                  key={player.participantId}
                  name={player.participantName ?? ''}
                  email={player.participantEmail ?? ''}
                  phone={player.participantPhone ?? ''}
                />
              ))
            ) : (
              <EmptyPlayersContainer>No unpaired players</EmptyPlayersContainer>
            )
          ) : (
            <PairedPlayersContainer>
              {tournamentDetails?.pairedParticipants &&
              tournamentDetails.pairedParticipants.length > 0 ? (
                tournamentDetails.pairedParticipants.map((team, index) => (
                  <PairedPlayerCard
                    key={team.teamId}
                    pairNumber={index + 1}
                    player1={team.participantName ?? ''}
                    player2={team.partnerName ?? ''}
                  />
                ))
              ) : (
                <EmptyPlayersContainer>No paired players</EmptyPlayersContainer>
              )}
            </PairedPlayersContainer>
          )}
        </ScrollContainer>
      </BodyContainer>

      <ButtonContainer>
        <GenerateButton
          type="primary"
          onClick={() => setIsBracketModalVisible(true)}
          height="44px"
        >
          Generate brackets
          {tournamentDetails?.totalUnpairedCount &&
          tournamentDetails?.totalUnpairedCount > 0
            ? `(${tournamentDetails?.totalUnpairedCount} will
          be auto-paired)`
            : null}
        </GenerateButton>
      </ButtonContainer>

      {isBracketModalVisible && (
        <SportTypeModal
          visible={isBracketModalVisible}
          onProceed={handleGenerateBracket}
          closeModal={() => setIsBracketModalVisible(false)}
          tournamentType={parsedTournamentType}
        />
      )}
    </Container>
  );
};

// Styled Components
export const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: ${COLORS.white.primary};
`;

export const BodyContainer = styled.div`
  flex: 1;
  padding: 20px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const ToggleContainer = styled.div`
  display: flex;
  background-color: ${COLORS.grey.extraLight};
  border-radius: 20px;
  padding: 5px;
  width: 400px;
  justify-content: space-between;
  margin-bottom: 12px;
  align-self: center;
`;

const Section = styled.div<{ active: boolean }>`
  display: flex;
  align-items: center;
  padding: 4px 18px;
  border-radius: 20px;
  width: 50%;
  gap: 6px;
  cursor: pointer;
  background-color: ${(props) =>
    props.active ? COLORS.white.primary : 'transparent'};
  box-shadow: ${(props) =>
    props.active ? '0 2px 4px rgba(0, 0, 0, 0.1)' : 'none'};
  transition: all 0.2s ease;

  &:hover {
    background-color: ${(props) =>
      props.active ? COLORS.white.primary : COLORS.grey.lightMedium};
  }
`;

const SectionText = styled.span`
  font-size: ${TEXT_SIZES.SM}px;
  color: ${COLORS.black.primary};
  font-weight: ${FONT_WEIGHTS.MEDIUM};
`;

const Badge = styled.div`
  margin-left: 8px;
  background-color: ${COLORS.skyBlue.primary};
  border-radius: 8px;
  min-width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 6px;
`;

const BadgeText = styled.span`
  color: ${COLORS.white.primary};
  font-weight: ${FONT_WEIGHTS.BOLD};
  font-size: ${TEXT_SIZES.XS}px;
`;

export const ScrollContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding-right: 10px;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: ${COLORS.grey.extraLight};
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${COLORS.grey.secondary};
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${COLORS.grey.primary};
  }
`;

const PairedPlayersContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  width: 100%;
`;

const EmptyPlayersContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  padding-top: 120px;
  font-size: ${TEXT_SIZES.MD}px;
`;

const PairedCard = styled.div`
  background-color: ${COLORS.skyBlue.light};
  border-radius: 4px;
  border: 1px solid ${COLORS.skyBlue.primary};
  min-height: 70px;
  padding-left: 16px;
  padding-right: 16px;
  padding-top: 8px;
  padding-bottom: 8px;
  margin-top: 8px;
  margin-bottom: 8px;
  align-items: center;
  display: flex;
  width: calc(33% - 17px);
`;

const NumberCircle = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 16px;
  background-color: ${COLORS.blue.primary};
  justify-content: center;
  display: flex;
  align-items: center;
  margin-right: 14px;
`;

const NumberText = styled.span`
  color: ${COLORS.white.primary};
  font-weight: ${FONT_WEIGHTS.BOLD};
  font-size: ${TEXT_SIZES.XS}px;
`;

const NameText = styled.span`
  color: ${COLORS.black.primary};
  font-size: ${TEXT_SIZES.SM}px;
`;

const PlayerCardContainer = styled.div`
  display: flex;
  background-color: ${COLORS.white.primary};
  border-radius: 8px;
  padding: 16px;
  margin: 20px 2px;
  align-items: flex-start;
  border-width: 1px;

  box-shadow: 0 0 8px 0 ${COLORS.grey.lightMedium};
`;

const AvatarContainer = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 21px;
  background-color: ${COLORS.grey.secondaryLight};
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 14px;
`;

const InitialsText = styled.span`
  font-size: ${TEXT_SIZES.SM}px;
  color: ${COLORS.grey.dark};
  font-weight: ${FONT_WEIGHTS.MEDIUM};
`;

const PlayerInfoContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const PlayerNameText = styled.span`
  font-size: ${TEXT_SIZES.MD}px;
  font-weight: ${FONT_WEIGHTS.MEDIUM};
  color: ${COLORS.black.primary};
  margin-bottom: 2px;
`;

const PlayerEmailText = styled.span`
  font-size: ${TEXT_SIZES.SM}px;
  color: ${COLORS.grey.secondary};
  margin-bottom: 2px;
`;
const PlayerPhoneText = styled.span`
  font-size: ${TEXT_SIZES.SM}px;
  color: ${COLORS.grey.secondary};
`;

export const ButtonContainer = styled.div`
  padding: 20px;
  align-self: flex-end;
`;

export const GenerateButton = styled(CustomButton)`
  height: 44px;
  padding: 0 40px;
`;

export default DoublesPlayersDetails;
